import { useEffect, useState } from "react";

import { Box, HStack } from "@chakra-ui/react";
import { ClientGateway } from "@kex/api";
import { useIntl } from "react-intl";

import { getNetworkSlugName } from "@shared/libs/crypto";
import { buildRateText, useFormatAmount } from "@shared/libs/format";
import { CellPanel } from "@shared/ui/atoms";
import { Typography } from "@shared/ui/chakra";

import { transactionRateMessages } from "./messages";

type Data = {
  rate: string;
  total: string;
};

export type RateData = {
  baseAssetCode: string;
  getAssetCode: string;
  getAssetNetwork?: ClientGateway.NetworkSlugsEnum;
  getData: () => Data;
  quoteAssetCode: string;
  type: "BUY" | "EXCHANGE" | "SELL";
};

export const TransactionRate = ({
  baseAssetCode,
  getAssetCode,
  getAssetNetwork,
  getData,
  quoteAssetCode,
}: RateData) => {
  const { formatMessage } = useIntl();
  const { formatAmount, formatAmountWithCode } = useFormatAmount();
  const networkName = getNetworkSlugName(getAssetNetwork);
  const networkLabel = networkName
    ? formatMessage(networkName)
    : getAssetNetwork;
  const [data, setData] = useState<Data>(getData());

  useEffect(() => {
    const time = setInterval(() => {
      setData(getData());
    }, 5000);

    return () => {
      clearInterval(time);
    };
  }, [getData]);
  return (
    <CellPanel>
      <Box px="16px" py="4px" w="full">
        <HStack
          alignItems="center"
          gap="12px"
          justifyContent="space-between"
          py="12px"
          w="full"
        >
          <Typography textStyle="body2">
            {formatMessage(transactionRateMessages.rate)}
          </Typography>
          <Typography fontWeight="medium" textStyle="subtitle2">
            {buildRateText({
              baseAssetCode,
              quoteAssetCode,
              rate: formatAmount(data?.rate),
            })}
          </Typography>
        </HStack>
        <Box backgroundColor="border.subtle" height="1px" />
        <HStack
          alignItems="center"
          gap="12px"
          justifyContent="space-between"
          py="12px"
          w="full"
        >
          <Typography textStyle="body2">
            {formatMessage(transactionRateMessages.get)}
          </Typography>
          <Typography as="p" textStyle="subtitle2">
            <Typography as="span" fontWeight="medium">
              {formatAmountWithCode(data?.total, getAssetCode)}
            </Typography>
            {getAssetNetwork && (
              <Typography as="span" color="text.fg_muted">
                {` • ${networkLabel}`}
              </Typography>
            )}
          </Typography>
        </HStack>
      </Box>
    </CellPanel>
  );
};
